import type {
  EmailSharePreviewInput,
  EmailShareTemplateContract,
  EmbedSnippetContract,
  ShareSettingsContract,
  TrackingLinkContract,
  TrackingLinkInput,
} from '@/contracts/share'
import {
  buildMockEmailSharePreview,
  buildMockTrackingLink,
  deleteMockEmailShareTemplate,
  getMockShareSettings,
  listMockEmailShareRecipients,
  listMockEmailShareTemplates,
  listMockEmbedSnippets,
  listMockTrackingLinks,
  saveMockEmailShareTemplate,
  updateMockShareSettings,
} from '@/lib/mock-api/share'
import { rebasePublicFormUrl } from '@/lib/public-form-url'
import { apiOrMock, buildQuery, requestApiEnvelope, toSourceMeta } from './http'

function withCurrentShareOrigin(settings: ShareSettingsContract): ShareSettingsContract {
  return {
    ...settings,
    publicUrl: rebasePublicFormUrl(settings.publicUrl, settings.formId),
    qrCodeUrl: rebasePublicFormUrl(settings.qrCodeUrl, settings.formId),
  }
}

export async function getShareSettings(formId: string): Promise<ShareSettingsContract> {
  return apiOrMock({
    label: 'getShareSettings',
    api: async () => {
      const envelope = await requestApiEnvelope<ShareSettingsContract>(`/api/forms/${formId}/share`)
      return withCurrentShareOrigin({
        ...envelope.data,
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      })
    },
    mock: async () => withCurrentShareOrigin(await getMockShareSettings(formId)),
  })
}

export async function updateShareSettings(
  formId: string,
  patch: Partial<Omit<ShareSettingsContract, 'formId' | 'publicUrl' | 'qrCodeUrl' | 'sourceMeta'>>,
): Promise<ShareSettingsContract> {
  return apiOrMock({
    label: 'updateShareSettings',
    api: async () => {
      const envelope = await requestApiEnvelope<ShareSettingsContract>(
        `/api/forms/${formId}/share`,
        {
          method: 'PATCH',
          body: JSON.stringify(patch),
        },
      )
      return withCurrentShareOrigin({
        ...envelope.data,
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      })
    },
    mock: async () => withCurrentShareOrigin(await updateMockShareSettings(formId, patch)),
  })
}

export async function listEmbedSnippets(formId: string): Promise<{
  data: EmbedSnippetContract[]
  meta: ReturnType<typeof toSourceMeta>
}> {
  return apiOrMock({
    label: 'listEmbedSnippets',
    api: async () => {
      const envelope = await requestApiEnvelope<EmbedSnippetContract[]>(
        `/api/forms/${formId}/share/embed-snippets`,
      )
      return {
        data: envelope.data,
        meta: toSourceMeta(envelope.meta),
      }
    },
    mock: () => listMockEmbedSnippets(formId),
  })
}

export async function buildTrackingLink(input: TrackingLinkInput) {
  return apiOrMock({
    label: 'buildTrackingLink',
    api: async () => {
      const { formId, ...body } = input
      const envelope = await requestApiEnvelope<TrackingLinkContract>(
        `/api/forms/${formId}/share/tracking-links`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      )
      return envelope.data
    },
    mock: () => buildMockTrackingLink(input),
  })
}

export async function listTrackingLinks(formId: string) {
  return apiOrMock({
    label: 'listTrackingLinks',
    api: async () => {
      const envelope = await requestApiEnvelope<TrackingLinkContract[]>(
        `/api/forms/${formId}/share/tracking-links${buildQuery({})}`,
      )
      return {
        data: envelope.data,
        meta: toSourceMeta(envelope.meta),
      }
    },
    mock: () => listMockTrackingLinks(formId),
  })
}

export async function listEmailShareTemplates(formId: string) {
  return apiOrMock({
    label: 'listEmailShareTemplates',
    api: async () => {
      const envelope = await requestApiEnvelope<EmailShareTemplateContract[]>(
        `/api/forms/${formId}/share/email-templates`,
      )
      return {
        data: envelope.data,
        meta: toSourceMeta(envelope.meta),
      }
    },
    mock: () => listMockEmailShareTemplates(formId),
  })
}

export async function saveEmailShareTemplate(formId: string, template: EmailShareTemplateContract) {
  return apiOrMock({
    label: 'saveEmailShareTemplate',
    api: async () => {
      const envelope = await requestApiEnvelope<EmailShareTemplateContract[]>(
        `/api/forms/${formId}/share/email-templates/${template.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify(template),
        },
      )
      return {
        data: envelope.data,
        meta: toSourceMeta(envelope.meta),
      }
    },
    mock: async () => ({
      data: await saveMockEmailShareTemplate(formId, template),
      meta: toSourceMeta({ source: 'synthetic' }),
    }),
  })
}

export async function deleteEmailShareTemplate(formId: string, templateId: string) {
  return apiOrMock({
    label: 'deleteEmailShareTemplate',
    api: async () => {
      const envelope = await requestApiEnvelope<EmailShareTemplateContract[]>(
        `/api/forms/${formId}/share/email-templates/${templateId}`,
        { method: 'DELETE' },
      )
      return {
        data: envelope.data,
        meta: toSourceMeta(envelope.meta),
      }
    },
    mock: async () => ({
      data: await deleteMockEmailShareTemplate(formId, templateId),
      meta: toSourceMeta({ source: 'synthetic' }),
    }),
  })
}

export async function listEmailShareRecipients() {
  return apiOrMock({
    label: 'listEmailShareRecipients',
    api: async () => {
      const envelope = await requestApiEnvelope<
        Awaited<ReturnType<typeof listMockEmailShareRecipients>>['data']
      >('/api/forms/share/email-recipients')
      return {
        data: envelope.data,
        meta: toSourceMeta(envelope.meta),
      }
    },
    mock: () => listMockEmailShareRecipients(),
  })
}

export async function buildEmailSharePreview(input: EmailSharePreviewInput) {
  return apiOrMock({
    label: 'buildEmailSharePreview',
    api: async () => {
      const { formId, ...body } = input
      const envelope = await requestApiEnvelope<
        Awaited<ReturnType<typeof buildMockEmailSharePreview>>
      >(`/api/forms/${formId}/share/email/preview`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      return envelope.data
    },
    mock: () => buildMockEmailSharePreview(input),
  })
}
